/**
 * Product card 3D tilt — cursor-following rotate on the sandwich slab.
 * Front hover glow + back satin coords update on pointer move.
 */
(function () {
  'use strict';

  if (window.__BK_CARD_TILT_INIT) return;
  window.__BK_CARD_TILT_INIT = true;

  const WRAPPER_SELECTOR =
    '.product-card-wrapper.product-card__flip-scene, .collection-card-wrapper.collection-card__tilt-scene';
  const FLIPPED_CLASS = 'is-cert-flipped';
  const TILT_ACTIVE_CLASS = 'is-tilt-active';
  const TILT_RETURNING_CLASS = 'is-tilt-returning';
  const FLIP_ANIMATING_CLASS = 'is-flip-animating';
  const FLIPPER_SELECTOR = '.product-card__flipper';
  const SLAB_SELECTOR = '.product-card__slab';
  const CARD_SURFACE_SELECTOR =
    '.product-card__face, .card--product-surface, .collection-card__cert-link';

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverCapableQuery = window.matchMedia('(hover: hover)');
  const mobileSliderQuery = window.matchMedia('(max-width: 989px)');

  let activeWrapper = null;
  let activeSlab = null;
  let bounds = null;
  let moveHandler = null;
  let lastPointer = { clientX: 0, clientY: 0 };
  let lastOnCardPointer = { clientX: 0, clientY: 0 };
  let pointerDownFreeze = false;
  let pointerDownWrapper = null;
  const flipSettleHandlers = new WeakMap();
  const flipSettleTimeouts = new WeakMap();
  const flipSettleCallbacks = new WeakMap();
  const flipStartedAt = new WeakMap();
  const flipTiltRafs = new WeakMap();
  const flipLeaveRafs = new WeakMap();
  const slabReturnHandlers = new WeakMap();
  const slabReturnTimeouts = new WeakMap();
  const slabReturnRafs = new WeakMap();
  const slabReturnFinalizeHandlers = new WeakMap();
  const slabReturnFinalizeTimeouts = new WeakMap();
  const lastTiltAxis = new WeakMap();
  const FLIP_SETTLE_TIMEOUT_MS = 800;
  const FLIP_SPIN_MS = 650;
  const FLIP_FLAT_HOLD_FRACTION = 0.25;
  const FLIP_MIN_SETTLE_MS = 600;
  const SLAB_RETURN_DURATION_MS = 220;
  const FLIPPER_RETURN_DURATION_MS = 250;
  let flipPointerOffCard = false;
  let leaveWrapper = null;

  document.addEventListener(
    'mousemove',
    (event) => {
      lastPointer = { clientX: event.clientX, clientY: event.clientY };
      if (activeWrapper?.classList.contains(FLIP_ANIMATING_CLASS)) {
        syncFlipLivePointer(activeWrapper, lastPointer);
      }
    },
    { passive: true }
  );

  function isCollectionFaceTilt(wrapper) {
    return Boolean(
      wrapper &&
        (wrapper.classList.contains('collection-card-face-tilt') ||
          wrapper.classList.contains('collection-card-flat-host') ||
          wrapper.classList.contains('collection-card-flat'))
    );
  }

  function slabEl(wrapper) {
    if (isCollectionFaceTilt(wrapper)) {
      const link = wrapper.querySelector('.collection-card__cert-link');
      if (link) return link;
      return wrapper.querySelector('.collection-card-flat__face');
    }
    return wrapper.querySelector(SLAB_SELECTOR);
  }

  function isCardSurfaceNode(wrapper, node) {
    if (!wrapper || !node) return false;
    if (node === wrapper) return false;
    if (!(node instanceof Element)) return false;
    const hit = node.closest(CARD_SURFACE_SELECTOR);
    return Boolean(hit && wrapper.contains(hit));
  }

  /* Product + CMS Trust: wrapper hosts hover — any pointer inside the shell counts. */
  function isHoverHostNode(wrapper, node) {
    if (!wrapper || !node) return false;
    if (isCollectionCertFoil(wrapper) || isProductFlipScene(wrapper)) {
      if (node === wrapper) return true;
      return wrapper.contains(node);
    }
    return isCardSurfaceNode(wrapper, node);
  }

  function tiltBoundsEl(wrapper) {
    const flipper = wrapper.querySelector(FLIPPER_SELECTOR);
    if (flipper) return flipper;
    const slab = slabEl(wrapper);
    if (slab) return slab;
    return wrapper;
  }

  function tiltBoundsRect(wrapper) {
    return tiltBoundsEl(wrapper).getBoundingClientRect();
  }

  function tiltConfig(wrapper) {
    const styles = getComputedStyle(wrapper);
    const divisor = parseFloat(styles.getPropertyValue('--product-card-tilt-divisor'));
    const angleFactor = parseFloat(styles.getPropertyValue('--product-card-tilt-angle-factor'));
    const maxAngle = parseFloat(styles.getPropertyValue('--product-card-tilt-max-angle'));
    const giltInset = parseFloat(styles.getPropertyValue('--product-card-gilt-inset'));
    const giltReveal = parseFloat(styles.getPropertyValue('--product-card-gilt-reveal'));
    return {
      divisor: Number.isFinite(divisor) && divisor > 0 ? divisor : 95,
      angleFactor: Number.isFinite(angleFactor) ? angleFactor : 1.6,
      maxAngle: Number.isFinite(maxAngle) && maxAngle > 0 ? maxAngle : 9,
      giltInset: Number.isFinite(giltInset) ? giltInset : 1,
      giltReveal: Number.isFinite(giltReveal) ? giltReveal : 2,
    };
  }

  function isMobileSlider(wrapper) {
    if (!mobileSliderQuery.matches) return false;
    const productGrid = wrapper.closest('.product-grid');
    if (productGrid && productGrid.classList.contains('slider')) return true;
    const collectionList = wrapper.closest('.collection-list');
    return Boolean(collectionList && collectionList.classList.contains('slider'));
  }

  const TILT_ANGLE_EPSILON = 0.2;

  function isCollectionCertFoil(wrapper) {
    return Boolean(wrapper && wrapper.classList.contains('cert-foil-live'));
  }

  function isProductFlipScene(wrapper) {
    return Boolean(wrapper && wrapper.classList.contains('product-card__flip-scene'));
  }

  /** Raised-cosine 0→1; peak velocity mid-ramp (CMS product flip envelope). */
  function bellEase01(u) {
    const x = Math.min(1, Math.max(0, u));
    return (1 - Math.cos(Math.PI * x)) / 2;
  }

  /** Tilt intensity over flip t∈[0,1]: ease → flat hold ~25% → ease. */
  function flipTiltIntensity(t, flatHoldFraction) {
    const hold = flatHoldFraction == null ? FLIP_FLAT_HOLD_FRACTION : flatHoldFraction;
    const halfFlat = hold / 2;
    const flatStart = 0.5 - halfFlat;
    const flatEnd = 0.5 + halfFlat;
    if (t <= 0) return 1;
    if (t >= 1) return 1;
    if (t >= flatStart && t <= flatEnd) return 0;
    if (t < flatStart) return 1 - bellEase01(t / flatStart);
    return bellEase01((t - flatEnd) / (1 - flatEnd));
  }

  function isInteriorCollectionFoil(wrapper) {
    if (!isCollectionCertFoil(wrapper)) return false;
    const item = wrapper.closest('.collection-list__item');
    if (!item) return false;
    const list = item.parentElement;
    if (!list || !list.classList.contains('collection-list--surface')) return false;
    const items = list.querySelectorAll(':scope > .collection-list__item');
    if (items.length < 3) return false;
    const index = Array.prototype.indexOf.call(items, item);
    return index > 0 && index < items.length - 1;
  }

  function foreshorteningCompensateScale(angleDeg, interior) {
    if (!Number.isFinite(angleDeg) || angleDeg < TILT_ANGLE_EPSILON) return 1;
    const angleRad = (angleDeg * Math.PI) / 180;
    const base = 1 / Math.cos(angleRad);
    return interior ? base * 1.003 : base;
  }

  function canTilt(wrapper) {
    if (!wrapper || !wrapper.matches(WRAPPER_SELECTOR)) return false;
    if (wrapper.classList.contains(FLIP_ANIMATING_CLASS)) return false;
    if (reducedMotionQuery.matches || !hoverCapableQuery.matches) return false;
    if (wrapper.closest('.section-divider')) return false;
    if (isMobileSlider(wrapper)) return false;
    return true;
  }

  function resetSlabEl(el) {
    if (!el) return;
    el.style.transform = '';
  }

  function cancelSlabReturnMotion(wrapper) {
    if (!wrapper) return;
    const rafId = slabReturnRafs.get(wrapper);
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      slabReturnRafs.delete(wrapper);
    }
    slabReturnHandlers.delete(wrapper);
    const timeoutId = slabReturnTimeouts.get(wrapper);
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      slabReturnTimeouts.delete(wrapper);
    }
  }

  function clearSlabReturnFinalize(wrapper) {
    if (!wrapper) return;
    const slab = slabEl(wrapper);
    const handler = slabReturnFinalizeHandlers.get(wrapper);
    if (slab && handler) {
      slab.removeEventListener('transitionend', handler);
    }
    slabReturnFinalizeHandlers.delete(wrapper);
    const finalizeTimeout = slabReturnFinalizeTimeouts.get(wrapper);
    if (finalizeTimeout != null) {
      clearTimeout(finalizeTimeout);
      slabReturnFinalizeTimeouts.delete(wrapper);
    }
  }

  function clearSlabReturn(wrapper) {
    if (!wrapper) return;
    cancelSlabReturnMotion(wrapper);
    clearSlabReturnFinalize(wrapper);
    wrapper.classList.remove(TILT_RETURNING_CLASS);
  }

  function easeOutCubic(t) {
    return 1 - (1 - t) ** 3;
  }

  function slabReturnDurationMs(slab) {
    if (!slab) return SLAB_RETURN_DURATION_MS;
    const seconds = parseFloat(getComputedStyle(slab).transitionDuration);
    const ms = seconds * 1000;
    return Number.isFinite(ms) && ms > 0 ? ms : SLAB_RETURN_DURATION_MS;
  }

  function isTransformSettled(el) {
    if (!el) return true;
    const transform = getComputedStyle(el).transform;
    if (!transform || transform === 'none') return true;
    if (transform === 'matrix(1, 0, 0, 1, 0, 0)') return true;
    if (
      transform ===
      'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)'
    ) {
      return true;
    }
    const match = transform.match(/^matrix3d\((.+)\)$/);
    if (!match) return false;
    const values = match[1].split(',').map((part) => parseFloat(part.trim()));
    if (values.length !== 16) return false;
    const offDiag = Math.max(
      Math.abs(values[1]),
      Math.abs(values[2]),
      Math.abs(values[4]),
      Math.abs(values[6]),
      Math.abs(values[8]),
      Math.abs(values[9])
    );
    return offDiag < 0.002;
  }

  function isSlabAtRestTransform(transform) {
    if (!transform) return true;
    if (transform === 'none') return true;
    return /,\s*0deg\)\s*$/.test(transform);
  }

  function leaveReturnDurationMs(wrapper) {
    if (!wrapper) return FLIPPER_RETURN_DURATION_MS;
    const flipper = wrapper.querySelector(FLIPPER_SELECTOR);
    if (flipper) {
      const seconds = parseFloat(getComputedStyle(flipper).transitionDuration);
      const ms = seconds * 1000;
      if (Number.isFinite(ms) && ms > 0) return ms;
    }
    return FLIPPER_RETURN_DURATION_MS;
  }

  function cardCenterPointer(wrapper) {
    const rect = tiltBoundsRect(wrapper);
    return {
      clientX: rect.left + rect.width / 2,
      clientY: rect.top + rect.height / 2,
    };
  }

  function cancelFlipLeaveEase(wrapper) {
    const raf = flipLeaveRafs.get(wrapper);
    if (raf != null) {
      cancelAnimationFrame(raf);
      flipLeaveRafs.delete(wrapper);
    }
  }

  /** Mid-flip leave: ease lastOnCardPointer to center (CMS — no one-frame snap). */
  function easeFlipPointerToCenter(wrapper) {
    if (!wrapper || flipLeaveRafs.has(wrapper)) return;
    flipPointerOffCard = true;
    const start = { ...lastOnCardPointer };
    const t0 = performance.now();
    const durationMs = leaveReturnDurationMs(wrapper);

    const tick = (now) => {
      if (!wrapper.classList.contains(FLIP_ANIMATING_CLASS) || !flipPointerOffCard) {
        flipLeaveRafs.delete(wrapper);
        return;
      }
      const center = cardCenterPointer(wrapper);
      const t = Math.min(1, (now - t0) / durationMs);
      const eased = easeOutCubic(t);
      lastOnCardPointer = {
        clientX: start.clientX + (center.clientX - start.clientX) * eased,
        clientY: start.clientY + (center.clientY - start.clientY) * eased,
      };
      if (t < 1) {
        flipLeaveRafs.set(wrapper, requestAnimationFrame(tick));
        return;
      }
      flipLeaveRafs.delete(wrapper);
      lastOnCardPointer = cardCenterPointer(wrapper);
    };

    flipLeaveRafs.set(wrapper, requestAnimationFrame(tick));
  }

  function completeSlabRest(wrapper, options = {}) {
    const skipTransform = Boolean(options.skipTransform);
    if (!wrapper) return;
    if (activeWrapper === wrapper) {
      clearSlabReturnFinalize(wrapper);
      cancelSlabReturnMotion(wrapper);
      wrapper.classList.remove(TILT_RETURNING_CLASS);
      return;
    }

    cancelSlabReturnMotion(wrapper);
    clearSlabReturnFinalize(wrapper);
    const slab = slabEl(wrapper);
    const last = lastTiltAxis.get(wrapper);

    if (
      !skipTransform &&
      slab &&
      last &&
      slab.style.transform &&
      !isSlabAtRestTransform(slab.style.transform)
    ) {
      slab.style.transform = `rotate3d(${last.axisX / last.divisor}, ${last.axisY / last.divisor}, 0, 0deg)`;
    }

    lastTiltAxis.delete(wrapper);
    wrapper.classList.remove(TILT_ACTIVE_CLASS);
    wrapper.classList.remove(TILT_RETURNING_CLASS);

    if (!isCollectionFaceTilt(wrapper)) {
      resetLayerScale(wrapper);
      resetGiltShift(wrapper);
      if (slab) resetSlabEl(slab);
    } else if (slab) {
      resetSlabEl(slab);
    }

    resetReflectivity(wrapper);
  }

  function scheduleSlabRestClassCleanup(wrapper) {
    if (!wrapper) return;
    const slab = slabEl(wrapper);
    if (!slab) {
      completeSlabRest(wrapper, { skipTransform: true });
      return;
    }
    if (slabReturnFinalizeHandlers.has(wrapper)) return;

    const durationMs = slabReturnDurationMs(slab) + 40;
    const onEnd = (event) => {
      if (event.target !== slab || event.propertyName !== 'transform') return;
      clearSlabReturnFinalize(wrapper);
      completeSlabRest(wrapper, { skipTransform: true });
    };

    slabReturnFinalizeHandlers.set(wrapper, onEnd);
    slab.addEventListener('transitionend', onEnd);
    slabReturnFinalizeTimeouts.set(
      wrapper,
      setTimeout(() => {
        if (slabReturnFinalizeHandlers.has(wrapper)) {
          clearSlabReturnFinalize(wrapper);
          completeSlabRest(wrapper, { skipTransform: true });
        }
      }, durationMs)
    );
  }

  function finishSlabReturn(wrapper) {
    completeSlabRest(wrapper);
  }

  function animateSlabToRest(wrapper) {
    if (!wrapper) return;
    const slab = slabEl(wrapper);
    if (!slab) {
      finishSlabReturn(wrapper);
      return;
    }
    if (slabReturnRafs.has(wrapper)) return;

    cancelFlipLeaveEase(wrapper);
    clearSlabReturnFinalize(wrapper);
    /* Drive tilt+gilt+sheen per-frame; CSS flipper owns lift/scale return (CMS). */
    slab.style.transition = 'none';

    const rect = tiltBoundsRect(wrapper);
    const startX = lastOnCardPointer.clientX || lastPointer.clientX;
    const startY = lastOnCardPointer.clientY || lastPointer.clientY;
    const durationMs = isProductFlipScene(wrapper)
      ? leaveReturnDurationMs(wrapper)
      : slabReturnDurationMs(slab);
    const startTime = performance.now();

    wrapper.classList.add(TILT_RETURNING_CLASS);
    wrapper.classList.add(TILT_ACTIVE_CLASS);
    slabReturnHandlers.set(wrapper, true);

    const tick = (now) => {
      if (!wrapper.classList.contains(TILT_RETURNING_CLASS)) {
        slabReturnRafs.delete(wrapper);
        return;
      }
      const center = cardCenterPointer(wrapper);
      const t = Math.min(1, (now - startTime) / durationMs);
      const eased = easeOutCubic(t);
      const point = {
        clientX: startX + (center.clientX - startX) * eased,
        clientY: startY + (center.clientY - startY) * eased,
      };
      lastOnCardPointer = point;
      applyTilt(wrapper, slab, point, rect);

      if (t < 1) {
        slabReturnRafs.set(wrapper, requestAnimationFrame(tick));
        return;
      }

      slabReturnRafs.delete(wrapper);
      slab.style.transition = '';
      completeSlabRest(wrapper, { skipTransform: true });
    };

    slabReturnRafs.set(wrapper, requestAnimationFrame(tick));
  }

  function resetSlab(wrapper) {
    resetSlabEl(slabEl(wrapper));
  }

  function resetAllTilt(wrapper) {
    clearSlabReturn(wrapper);
    resetSlab(wrapper);
    if (isCollectionFaceTilt(wrapper)) return;
    resetReflectivity(wrapper);
    resetGiltShift(wrapper);
    resetLayerScale(wrapper);
  }

  function resetGiltShift(wrapper) {
    if (!wrapper) return;
    wrapper.style.removeProperty('--product-card-gilt-shift-x');
    wrapper.style.removeProperty('--product-card-gilt-shift-y');
  }

  function resetLayerScale(wrapper) {
    if (!wrapper) return;
    wrapper.style.removeProperty('--product-card-layer-scale');
  }

  function frontGlowEl(wrapper) {
    return wrapper.querySelector('.product-card__face--front .card.card--product-surface');
  }

  function backSheenEl(wrapper) {
    return (
      wrapper.querySelector('.cert-card-back[data-cert-card-back]') ||
      wrapper.querySelector('.cert-card-back')
    );
  }

  function clampPercent(n) {
    return Math.min(100, Math.max(0, Math.round(n)));
  }

  function updateReflectivity(wrapper, event, rect) {
    const x = clampPercent(((event.clientX - rect.left) / rect.width) * 100);
    const y = clampPercent(((event.clientY - rect.top) / rect.height) * 100);
    const flipped = wrapper.classList.contains(FLIPPED_CLASS);
    /* Midplane gilt lives in flipper space: rotateY(180) mirrors local X.
       Cert-back content is double-rotated (upright) — keep raw on .cert-card-back. */
    const wrapperSheenX =
      isProductFlipScene(wrapper) && flipped ? 100 - x : x;

    const front = frontGlowEl(wrapper);
    if (front) {
      front.style.setProperty('--product-card-glow-x', `${x}%`);
      front.style.setProperty('--product-card-glow-y', `${y}%`);
    }

    /* Wrapper sheen so midplane gilt (sibling of face) can resolve gilt-sheen-position. */
    wrapper.style.setProperty('--product-card-sheen-x', `${wrapperSheenX}%`);
    wrapper.style.setProperty('--product-card-sheen-y', `${y}%`);

    const back = backSheenEl(wrapper);
    if (back) {
      back.style.setProperty('--product-card-sheen-x', `${x}%`);
      back.style.setProperty('--product-card-sheen-y', `${y}%`);
      back.style.setProperty(
        '--product-card-pointer-from-center',
        String(Math.min(1, Math.max(0, Math.hypot(x - 50, y - 50) / 50)))
      );
      back.style.setProperty('--product-card-pointer-from-top', String(y / 100));
      back.style.setProperty('--product-card-pointer-from-left', String(x / 100));
    }
  }

  function applySlabReturnTransform(wrapper, slab) {
    const { divisor } = tiltConfig(wrapper);
    const last = lastTiltAxis.get(wrapper);
    if (last && slab) {
      slab.style.transform = `rotate3d(${last.axisX / divisor}, ${last.axisY / divisor}, 0, 0deg)`;
    }
  }

  function resetReflectivity(wrapper) {
    const front = frontGlowEl(wrapper);
    if (front) {
      front.style.removeProperty('--product-card-glow-x');
      front.style.removeProperty('--product-card-glow-y');
    }

    wrapper.style.removeProperty('--product-card-sheen-x');
    wrapper.style.removeProperty('--product-card-sheen-y');

    const back = backSheenEl(wrapper);
    if (back) {
      back.style.removeProperty('--product-card-sheen-x');
      back.style.removeProperty('--product-card-sheen-y');
      back.style.removeProperty('--product-card-pointer-from-center');
      back.style.removeProperty('--product-card-pointer-from-top');
      back.style.removeProperty('--product-card-pointer-from-left');
    }
  }

  function applyTilt(wrapper, slab, event, rect) {
    if (wrapper.classList.contains(FLIP_ANIMATING_CLASS)) return;
    updateReflectivity(wrapper, event, rect);

    const collectionFoil = isCollectionCertFoil(wrapper);
    const { divisor, angleFactor, maxAngle, giltInset, giltReveal } = tiltConfig(wrapper);
    const leftX = event.clientX - rect.left;
    const topY = event.clientY - rect.top;
    const center = {
      x: leftX - rect.width / 2,
      y: topY - rect.height / 2,
    };
    const distance = Math.sqrt(center.x ** 2 + center.y ** 2);
    const rawAngle = Math.log(Math.max(distance, 1)) * angleFactor;
    const angle = Math.min(rawAngle, maxAngle);

    /*
     * Product flip-scene (CMS): axisX = cy both faces; axisY = flipped ? cx : -cx.
     * Trust / collection foil: keep prior screen axes (flipped → -cy, -cx).
     */
    const flipped = wrapper.classList.contains(FLIPPED_CLASS);
    let axisX;
    let axisY;
    if (isProductFlipScene(wrapper)) {
      axisX = center.y;
      axisY = flipped ? center.x : -center.x;
    } else {
      axisX = flipped ? -center.y : center.y;
      axisY = -center.x;
    }

    if (angle >= TILT_ANGLE_EPSILON) {
      lastTiltAxis.set(wrapper, { axisX, axisY, divisor });
    }

    /* Flat canary: tilt face inside perspective host; no transform until real angle. */
    if (isCollectionFaceTilt(wrapper)) {
      if (angle < TILT_ANGLE_EPSILON) {
        if (wrapper.classList.contains(TILT_RETURNING_CLASS)) {
          applySlabReturnTransform(wrapper, slab);
          return;
        }
        if (isSlabAtRestTransform(slab.style.transform)) return;
        animateSlabToRest(wrapper);
        return;
      }
      if (!wrapper.classList.contains(TILT_RETURNING_CLASS)) {
        clearSlabReturn(wrapper);
      }
      wrapper.classList.add(TILT_ACTIVE_CLASS);
      slab.style.transform = `rotate3d(${axisX / divisor}, ${axisY / divisor}, 0, ${angle}deg)`;
      return;
    }

    /* Near-center: skip rotate3d(0deg) — compositing + sheen filter delta darkened
     * Made Safe rim (collection-set-hover-shrink v372). Real tilt from corner pull. */
    if (angle < TILT_ANGLE_EPSILON) {
      if (collectionFoil) {
        resetLayerScale(wrapper);
      }
      if (wrapper.classList.contains(TILT_RETURNING_CLASS)) {
        applySlabReturnTransform(wrapper, slab);
        return;
      }
      if (isSlabAtRestTransform(slab.style.transform)) return;
      animateSlabToRest(wrapper);
    } else {
      if (!wrapper.classList.contains(TILT_RETURNING_CLASS)) {
        clearSlabReturn(wrapper);
      }
      if (collectionFoil) {
        const compensate = foreshorteningCompensateScale(
          angle,
          isInteriorCollectionFoil(wrapper)
        );
        wrapper.style.setProperty('--product-card-layer-scale', String(compensate));
      }
      slab.style.transform = `rotate3d(${axisX / divisor}, ${axisY / divisor}, 0, ${angle}deg)`;
    }

    /* Midplane gilt XY peek — product + Trust collection (CMS Trust parity v385). */
    const nx = (leftX / rect.width - 0.5) * 2;
    const ny = (topY / rect.height - 0.5) * 2;
    const maxShift = giltInset + giltReveal;
    let sx = nx * maxShift;
    let sy = ny * maxShift;
    if (flipped) sx = -sx;
    wrapper.style.setProperty('--product-card-gilt-shift-x', `${sx}px`);
    wrapper.style.setProperty('--product-card-gilt-shift-y', `${sy}px`);
  }

  function clearFlipSettleListener(wrapper) {
    const flipper = wrapper.querySelector(FLIPPER_SELECTOR);
    const handler = flipSettleHandlers.get(wrapper);
    if (flipper && handler) {
      flipper.removeEventListener('transitionend', handler);
    }
    flipSettleHandlers.delete(wrapper);

    const timeoutId = flipSettleTimeouts.get(wrapper);
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      flipSettleTimeouts.delete(wrapper);
    }
    flipStartedAt.delete(wrapper);
    clearFlipTiltEnvelope(wrapper);
  }

  function clearFlipTiltEnvelope(wrapper) {
    const raf = flipTiltRafs.get(wrapper);
    if (raf != null) {
      cancelAnimationFrame(raf);
      flipTiltRafs.delete(wrapper);
    }
    cancelFlipLeaveEase(wrapper);
    const slab = slabEl(wrapper);
    if (slab) slab.style.transition = '';
  }

  function sampleProductTiltPose(wrapper, event, rect, flipped) {
    const { divisor, angleFactor, maxAngle } = tiltConfig(wrapper);
    const center = {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2,
    };
    const distance = Math.sqrt(center.x ** 2 + center.y ** 2);
    const rawAngle = Math.log(Math.max(distance, 1)) * angleFactor;
    const angle = Math.min(rawAngle, maxAngle);
    return {
      axisX: center.y,
      axisY: flipped ? center.x : -center.x,
      divisor,
      angle,
    };
  }

  function paintTiltPose(wrapper, slab, pose, intensity) {
    const angle = pose.angle * intensity;
    lastTiltAxis.set(wrapper, {
      axisX: pose.axisX,
      axisY: pose.axisY,
      divisor: pose.divisor,
    });
    slab.style.transform = `rotate3d(${pose.axisX / pose.divisor}, ${pose.axisY / pose.divisor}, 0, ${angle}deg)`;
  }

  function updateGiltShiftIntensity(wrapper, event, rect, intensity) {
    const { giltInset, giltReveal } = tiltConfig(wrapper);
    const leftX = event.clientX - rect.left;
    const topY = event.clientY - rect.top;
    const nx = (leftX / rect.width - 0.5) * 2;
    const ny = (topY / rect.height - 0.5) * 2;
    const flipped = wrapper.classList.contains(FLIPPED_CLASS);
    const maxShift = giltInset + giltReveal;
    const amount = Math.min(1, Math.max(0, intensity));
    let sx = nx * maxShift * amount;
    const sy = ny * maxShift * amount;
    if (flipped) sx = -sx;
    wrapper.style.setProperty('--product-card-gilt-shift-x', `${sx}px`);
    wrapper.style.setProperty('--product-card-gilt-shift-y', `${sy}px`);
  }

  function pointerInRect(rect, point) {
    return (
      point.clientX >= rect.left &&
      point.clientX <= rect.right &&
      point.clientY >= rect.top &&
      point.clientY <= rect.bottom
    );
  }

  /* Product hitbox is the card wrapper. Tilt math still uses the flipper rect. */
  function hoverHitRect(wrapper) {
    if (isProductFlipScene(wrapper)) return wrapper.getBoundingClientRect();
    return tiltBoundsRect(wrapper);
  }

  function pointerInHoverHit(wrapper, point) {
    return pointerInRect(hoverHitRect(wrapper), point);
  }

  function pointerInTiltBounds(wrapper, point) {
    return pointerInRect(tiltBoundsRect(wrapper), point);
  }

  function syncFlipLivePointer(wrapper, point) {
    if (!wrapper || !wrapper.classList.contains(FLIP_ANIMATING_CLASS)) return;
    const onHit = isProductFlipScene(wrapper)
      ? pointerInHoverHit(wrapper, point)
      : pointerInTiltBounds(wrapper, point);
    if (onHit) {
      cancelFlipLeaveEase(wrapper);
      flipPointerOffCard = false;
      lastOnCardPointer = { clientX: point.clientX, clientY: point.clientY };
      return;
    }
    if (!flipPointerOffCard) {
      easeFlipPointerToCenter(wrapper);
    }
  }

  function startContinuousFlipTiltEnvelope(wrapper, slab, startFlipped, endFlipped) {
    /* Rigid Y spin (gilt-flip-rigid-axis-v1): keep envelope RAF + reflectivity,
       but hold slab tilt + gilt XY at 0 for the whole flip. Hover tilt resumes
       in onFlipSettled. startFlipped/endFlipped kept for call-site stability. */
    void startFlipped;
    void endFlipped;
    slab.style.transition = 'none';
    const t0 = performance.now();

    const tick = (now) => {
      if (!wrapper.classList.contains(FLIP_ANIMATING_CLASS)) {
        flipTiltRafs.delete(wrapper);
        return;
      }
      const t = Math.min(1, (now - t0) / FLIP_SPIN_MS);
      const rect = tiltBoundsRect(wrapper);
      const pose = sampleProductTiltPose(wrapper, lastOnCardPointer, rect, false);
      paintTiltPose(wrapper, slab, pose, 0);
      updateReflectivity(wrapper, lastOnCardPointer, rect);
      updateGiltShiftIntensity(wrapper, lastOnCardPointer, rect, 0);

      if (t < 1) {
        flipTiltRafs.set(wrapper, requestAnimationFrame(tick));
      } else {
        flipTiltRafs.delete(wrapper);
        paintTiltPose(wrapper, slab, pose, 0);
        updateReflectivity(wrapper, lastOnCardPointer, rect);
        updateGiltShiftIntensity(wrapper, lastOnCardPointer, rect, 0);
      }
    };

    flipTiltRafs.set(wrapper, requestAnimationFrame(tick));
  }

  function onFlipSettled(wrapper) {
    if (!wrapper) return;
    if (!wrapper.classList.contains(FLIP_ANIMATING_CLASS)) return;
    clearFlipSettleListener(wrapper);
    wrapper.classList.remove(FLIP_ANIMATING_CLASS);
    pointerDownFreeze = false;
    const onSettled = flipSettleCallbacks.get(wrapper);
    flipSettleCallbacks.delete(wrapper);
    if (typeof onSettled === 'function') onSettled(wrapper);

    if (isProductFlipScene(wrapper) && activeWrapper === wrapper) {
      if (flipPointerOffCard || !pointerInTiltBounds(wrapper, lastPointer)) {
        flipPointerOffCard = false;
        stopTracking(true);
        return;
      }
      const slab = activeSlab || slabEl(wrapper);
      if (slab) {
        activeSlab = slab;
        bounds = tiltBoundsRect(wrapper);
        applyTilt(wrapper, slab, lastOnCardPointer, bounds);
      }
      return;
    }

    /* Entrance / non-hovered flips leave envelope pose at intensity 1; flatten. */
    if (!resumeIfHovered(wrapper)) {
      resetAllTilt(wrapper);
    }
  }

  function flipTransitionIsInstant(flipper) {
    if (reducedMotionQuery.matches) return true;
    const duration = parseFloat(getComputedStyle(flipper).transitionDuration);
    return !Number.isFinite(duration) || duration === 0;
  }

  function armFlipSettle(wrapper) {
    if (!wrapper.classList.contains(FLIP_ANIMATING_CLASS)) return;

    const flipper = wrapper.querySelector(FLIPPER_SELECTOR);
    if (!flipper || flipTransitionIsInstant(flipper)) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => onFlipSettled(wrapper));
      });
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== flipper || event.propertyName !== 'transform') return;
      const started = flipStartedAt.get(wrapper);
      const elapsed = started != null ? performance.now() - started : 0;
      if (started != null && elapsed < FLIP_MIN_SETTLE_MS) {
        const wait = Math.max(0, FLIP_MIN_SETTLE_MS - elapsed);
        setTimeout(() => {
          if (wrapper.classList.contains(FLIP_ANIMATING_CLASS)) onFlipSettled(wrapper);
        }, wait);
        return;
      }
      onFlipSettled(wrapper);
    };

    flipSettleHandlers.set(wrapper, onTransitionEnd);
    flipper.addEventListener('transitionend', onTransitionEnd);
    flipSettleTimeouts.set(
      wrapper,
      setTimeout(() => {
        if (wrapper.classList.contains(FLIP_ANIMATING_CLASS)) {
          onFlipSettled(wrapper);
        }
      }, FLIP_SETTLE_TIMEOUT_MS)
    );
  }

  /**
   * Product: beginFlip toggles .is-cert-flipped in parallel with tilt envelope
   * (ease → flat ~25% → ease). Persist .is-tilt-active through the spin.
   * Signature: onFlipStart(wrapper, beginFlip?, onSettled?)
   */
  function onFlipStart(wrapper, beginFlip, onSettled) {
    if (!wrapper) return;
    if (wrapper.classList.contains(FLIP_ANIMATING_CLASS)) return;

    clearFlipSettleListener(wrapper);
    flipSettleCallbacks.delete(wrapper);
    if (typeof onSettled === 'function') {
      flipSettleCallbacks.set(wrapper, onSettled);
    }

    if (!isProductFlipScene(wrapper)) {
      if (activeWrapper === wrapper) {
        stopTracking(false);
      } else {
        wrapper.classList.remove(TILT_ACTIVE_CLASS);
      }
      resetAllTilt(wrapper);
      resetReflectivity(wrapper);
      wrapper.classList.add(FLIP_ANIMATING_CLASS);
      if (typeof beginFlip === 'function') beginFlip();
      armFlipSettle(wrapper);
      return;
    }

    pointerDownFreeze = true;
    flipPointerOffCard = false;
    clearSlabReturn(wrapper);

    const slab = slabEl(wrapper);
    const wasFlipped = wrapper.classList.contains(FLIPPED_CLASS);

    if (activeWrapper === wrapper) {
      activeSlab = slab;
      bounds = null;
      wrapper.classList.add(TILT_ACTIVE_CLASS);
    }

    wrapper.classList.add(FLIP_ANIMATING_CLASS);
    const flipper = wrapper.querySelector(FLIPPER_SELECTOR);
    if (flipper) void flipper.offsetWidth;
    if (typeof beginFlip === 'function') beginFlip();
    const endFlipped = wrapper.classList.contains(FLIPPED_CLASS);
    flipStartedAt.set(wrapper, performance.now());

    if (slab) {
      startContinuousFlipTiltEnvelope(wrapper, slab, wasFlipped, endFlipped);
    }

    requestAnimationFrame(() => armFlipSettle(wrapper));
  }

  function stopTracking(resetTilt) {
    thawTiltForClick();
    const wrapper = activeWrapper;
    activeWrapper = null;
    activeSlab = null;
    bounds = null;
    if (moveHandler) {
      document.removeEventListener('mousemove', moveHandler);
      moveHandler = null;
    }
    if (!wrapper) return;

    if (isProductFlipScene(wrapper) && leaveWrapper === wrapper) {
      clearLeaveListener();
    }

    if (resetTilt) {
      /* Product CMS leave: keep sheen/gilt through return tween (do not clear first). */
      if (isProductFlipScene(wrapper)) {
        cancelFlipLeaveEase(wrapper);
        clearSlabReturn(wrapper);
        animateSlabToRest(wrapper);
        return;
      }
      if (!isCollectionFaceTilt(wrapper)) {
        resetReflectivity(wrapper);
        resetGiltShift(wrapper);
      }
      animateSlabToRest(wrapper);
      return;
    }

    cancelFlipLeaveEase(wrapper);
    wrapper.classList.remove(TILT_ACTIVE_CLASS);
    resetReflectivity(wrapper);
  }

  function freezeTiltForClick(wrapper) {
    if (!wrapper) return;
    pointerDownFreeze = true;
    pointerDownWrapper = wrapper;
    /* Product: hold tracking only — tilt envelope starts with flip (CMS). */
    if (isProductFlipScene(wrapper)) return;
    resetAllTilt(wrapper);
  }

  function thawTiltForClick() {
    pointerDownFreeze = false;
    pointerDownWrapper = null;
  }

  function rotateToMouse(event) {
    if (pointerDownFreeze) return;
    if (!activeWrapper || !activeSlab || !bounds) return;
    if (!canTilt(activeWrapper)) {
      stopTracking(true);
      return;
    }

    applyTilt(activeWrapper, activeSlab, event, tiltBoundsRect(activeWrapper));
    lastOnCardPointer = { clientX: event.clientX, clientY: event.clientY };
  }

  function startTracking(wrapper, event) {
    if (!canTilt(wrapper)) return;

    stopTracking(false);

    const slab = slabEl(wrapper);
    if (!slab) return;

    activeWrapper = wrapper;
    activeSlab = slab;
    bounds = tiltBoundsRect(wrapper);
    if (!isCollectionFaceTilt(wrapper)) {
      wrapper.classList.add(TILT_ACTIVE_CLASS);
    }
    moveHandler = rotateToMouse;
    document.addEventListener('mousemove', moveHandler, { passive: true });
    if (isProductFlipScene(wrapper)) bindLeaveListener(wrapper);
    /* Do not rotate3d on mouseover entry — off-center entry caused face-wide
       chrome shimmy + mid-row foreshortening concurrent with lift
       (product-card-lift-blur 2026-08-27-7). First tilt waits for mousemove. */
  }

  function resumeIfHovered(wrapper) {
    if (!canTilt(wrapper)) return false;
    const hit = document.elementFromPoint(lastPointer.clientX, lastPointer.clientY);
    if (!hit || !wrapper.contains(hit)) return false;
    startTracking(wrapper, lastPointer);
    return true;
  }

  function resetWrapper(wrapper) {
    if (!wrapper) return;
    clearFlipSettleListener(wrapper);
    flipSettleCallbacks.delete(wrapper);
    wrapper.classList.remove(FLIP_ANIMATING_CLASS);
    if (activeWrapper === wrapper) {
      stopTracking(true);
      return;
    }
    clearSlabReturn(wrapper);
    wrapper.classList.remove(TILT_ACTIVE_CLASS);
    resetAllTilt(wrapper);
  }

  function clearLeaveListener() {
    if (!leaveWrapper) return;
    leaveWrapper.removeEventListener('pointerleave', onWrapperPointerLeave);
    leaveWrapper = null;
  }

  function bindLeaveListener(wrapper) {
    if (!wrapper || leaveWrapper === wrapper) return;
    clearLeaveListener();
    leaveWrapper = wrapper;
    wrapper.addEventListener('pointerleave', onWrapperPointerLeave);
  }

  /* Product leave only when the pointer exits the wrapper shell (CMS). */
  function onWrapperPointerLeave(event) {
    if (event.pointerType === 'touch') return;
    const wrapper = leaveWrapper;
    if (!wrapper || activeWrapper !== wrapper || !isProductFlipScene(wrapper)) return;

    const point = { clientX: event.clientX, clientY: event.clientY };
    lastPointer = point;

    if (wrapper.classList.contains(FLIP_ANIMATING_CLASS)) {
      if (pointerInHoverHit(wrapper, point)) return;
      syncFlipLivePointer(wrapper, point);
      return;
    }

    const hit = document.elementFromPoint(point.clientX, point.clientY);
    if (hit && wrapper.contains(hit)) return;

    thawTiltForClick();
    stopTracking(true);
  }

  document.body.addEventListener('mouseover', (event) => {
    const wrapper = event.target.closest(WRAPPER_SELECTOR);
    if (!wrapper) return;

    const from = event.relatedTarget;
    if (
      from instanceof Node &&
      wrapper.contains(from) &&
      isHoverHostNode(wrapper, from)
    ) {
      return;
    }

    if (!isHoverHostNode(wrapper, event.target)) return;

    startTracking(wrapper, event);
  });

  document.body.addEventListener('mouseout', (event) => {
    const wrapper = event.target.closest(WRAPPER_SELECTOR);
    if (!wrapper || activeWrapper !== wrapper) return;
    /* Product leave is pointerleave on the wrapper (false mouseout from PE churn). */
    if (isProductFlipScene(wrapper)) return;

    const to = event.relatedTarget;
    if (to instanceof Node && wrapper.contains(to) && isHoverHostNode(wrapper, to)) return;

    /* Product mid-flip: keep chrome; drive envelope from card center (CMS). */
    if (
      isProductFlipScene(wrapper) &&
      wrapper.classList.contains(FLIP_ANIMATING_CLASS)
    ) {
      syncFlipLivePointer(wrapper, {
        clientX: lastPointer.clientX,
        clientY: lastPointer.clientY,
      });
      return;
    }

    thawTiltForClick();
    stopTracking(true);
  });

  document.addEventListener(
    'pointerdown',
    (event) => {
      const wrapper = event.target.closest(WRAPPER_SELECTOR);
      if (!wrapper) return;
      freezeTiltForClick(wrapper);
    },
    true
  );

  document.addEventListener('pointerup', thawTiltForClick, true);
  document.addEventListener('pointercancel', thawTiltForClick, true);

  window.productCardTilt = {
    reset: resetWrapper,
    stopAll: () => stopTracking(true),
    onFlipStart,
  };
})();
